import axios, { AxiosError, type AxiosInstance } from 'axios';
import { useAuthStore } from '../store/auth.store';

// Instância única — PADRAO_DE_CODIGO §Painel: NUNCA `axios.create()` em outros lugares.
// Interceptor injeta Bearer token de auth.store; refresh automático em 401.
const api: AxiosInstance = axios.create({
  // baseURL:
  //   • BUILD DE PROD com VITE_API_URL (ex. https://oraculo-api.golevelgo.com/api/v1)
  //     → o bundle chama a API absoluta. É o caminho de prod: o painel é servido
  //     em oraculo.golevelgo.com e o CORS da API libera ESSA origem (não libera
  //     localhost — por isso a URL absoluta é restrita ao build de prod).
  //   • dev (ou prod sem a var) → '/api/v1' relativo. Em dev o Vite encaminha
  //     /api via proxy (same-origin, sem CORS). VITE_API_URL em dev serve só ao
  //     alvo do proxy (vite.config), não ao cliente. O refresh
  //     (api.defaults.baseURL) segue este mesmo valor.
  baseURL:
    import.meta.env.PROD && import.meta.env.VITE_API_URL
      ? import.meta.env.VITE_API_URL
      : '/api/v1',
  timeout: 15000,
});

// Request interceptor — Bearer token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — refresh em 401 (uma vez)
let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function flushQueue(token: string | null) {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config!;
    const status = error.response?.status;

    if (status === 401 && !(original as { _retry?: boolean })._retry) {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((newToken) => {
            if (!newToken) {
              reject(error);
              return;
            }
            original.headers!.Authorization = `Bearer ${newToken}`;
            (original as { _retry?: boolean })._retry = true;
            resolve(api.request(original));
          });
        });
      }

      isRefreshing = true;
      try {
        const refreshResp = await axios.post(`${api.defaults.baseURL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const newAccess = refreshResp.data.access_token as string;
        const newRefresh = refreshResp.data.refresh_token as string;
        useAuthStore.getState().setTokens(newAccess, newRefresh);
        flushQueue(newAccess);

        original.headers!.Authorization = `Bearer ${newAccess}`;
        (original as { _retry?: boolean })._retry = true;
        return api.request(original);
      } catch (refreshErr) {
        flushQueue(null);
        useAuthStore.getState().logout();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
